import React, { useState, useEffect } from 'react';
import { Calendar, Clock, ChevronLeft, ChevronRight, Download, Users, Plus, X } from 'lucide-react';
import api from '../api/axiosConfig';
import { useToast } from '../hooks/useToast';
import { useNotification } from './ui/NotificationModal';

const VardiyaYonetimi = () => {
  const { showSuccess, showError, showWarning } = useToast();
  const showNotification = useNotification();
  const [driversWithShifts, setDriversWithShifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: ''
  });

  // Haftanın başlangıç tarihini hesapla (bu hafta pazartesi)
  const getWeekStart = () => {
    const now = new Date();
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1); // Pazartesi
    return new Date(now.setDate(diff));
  };

  const [currentWeekStart, setCurrentWeekStart] = useState(getWeekStart());
  const [editingShifts, setEditingShifts] = useState({});
  const [offDays, setOffDays] = useState({}); // İzinli günler: {driverId-dayId: true}
  const [isWeekSaved, setIsWeekSaved] = useState(false); // Bu hafta kaydedilmiş mi?
  const [showAddModal, setShowAddModal] = useState(false);

  // Haftalık günleri tarihlerle birlikte hesapla
  const getWeekDaysWithDates = () => {
    const weekStart = new Date(currentWeekStart);
    return [
      { id: 0, name: 'Pazartesi', short: 'PZT' },
      { id: 1, name: 'Salı', short: 'SAL' },
      { id: 2, name: 'Çarşamba', short: 'ÇAR' },
      { id: 3, name: 'Perşembe', short: 'PER' },
      { id: 4, name: 'Cuma', short: 'CUM' },
      { id: 5, name: 'Cumartesi', short: 'CMT' },
      { id: 6, name: 'Pazar', short: 'PAZ' }
    ].map(day => {
      const date = new Date(weekStart);
      date.setDate(weekStart.getDate() + day.id);
      return {
        ...day,
        date: date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' }),
        // Local timezone için doğru tarih - UTC kullanma!
        fullDate: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
      };
    });
  };

  const weekDays = getWeekDaysWithDates();

  // Hafta değiştiğinde vardiyaları yükle
  useEffect(() => {
    loadWeeklyShifts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWeekStart]);

  const loadWeeklyShifts = async () => {
    try {
      setLoading(true);
      
      // Şoför listesini al
      const driversResponse = await api.get('/api/admin/drivers');
      const driversList = driversResponse.data.drivers || driversResponse.data;
      
      // Haftalık planı çek (Local timezone)
      const weekStartStr = `${currentWeekStart.getFullYear()}-${String(currentWeekStart.getMonth() + 1).padStart(2, '0')}-${String(currentWeekStart.getDate()).padStart(2, '0')}`;
      const weeklyPlanResponse = await api.get(`/api/shifts/weekly-plan?week_start=${weekStartStr}`);
      const { is_saved, shifts: weeklyShifts } = weeklyPlanResponse.data;
      
      setIsWeekSaved(is_saved);
      
      if (is_saved && weeklyShifts.length > 0) {
        // Kaydedilmiş plan var - vardiyaları şoförlere göre organize et
        const driversMap = {};
        
        // Tüm şoförler için boş obje oluştur
        driversList.forEach(driver => {
          driversMap[driver.id] = {
            ...driver,
            weeklyShifts: {}
          };
        });
        
        // Vardiyaları ekle
        weeklyShifts.forEach(shift => {
          if (driversMap[shift.driver_id]) {
            const shiftDate = new Date(shift.shift_date);
            const dayOfWeek = shiftDate.getDay();
            const adjustedDay = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Pazar=6, Pazartesi=0
            
            driversMap[shift.driver_id].weeklyShifts[adjustedDay] = {
              start_time: shift.start_time,
              end_time: shift.end_time,
              shift_type: shift.shift_type
            };
          }
        });
        
        // İzinli günleri belirle (vardiya kaydı olmayan günler)
        const newOffDays = {};
        driversList.forEach(driver => {
          // 7 günü kontrol et (0-6: Pazartesi-Pazar)
          for (let dayId = 0; dayId < 7; dayId++) {
            const hasShift = driversMap[driver.id]?.weeklyShifts[dayId];
            if (!hasShift) {
              // Bu gün için vardiya yok = izinli
              const key = `${driver.id}-${dayId}`;
              newOffDays[key] = true;
            }
          }
        });
        
        setOffDays(newOffDays);
        setDriversWithShifts(Object.values(driversMap));
      } else {
        // Kayıt yok - boş plan göster
        const driversWithEmpty = driversList.map(driver => ({
          ...driver,
          weeklyShifts: {}
        }));
        setOffDays({});  // İzinli durumlarını temizle
        setDriversWithShifts(driversWithEmpty);
      }
      
    } catch (error) {
      console.error('Haftalık vardiyalar yüklenemedi:', error);
      showError('Haftalık vardiyalar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };


  const handleSubmit = async () => {
    try {
      setLoading(true);

      // Form validasyonu
      if (!formData.name.trim()) {
        showError('Ad Soyad zorunludur');
        return;
      }
      if (!formData.phone.trim()) {
        showError('Telefon numarası zorunludur');
        return;
      }
      
      // Telefon numarası formatını kontrol et
      const phoneDigits = formData.phone.replace(/\D/g, '');
      if (phoneDigits.length < 10) {
        showError('Geçerli bir telefon numarası giriniz (en az 10 haneli)');
        return;
      }

      // 1. Kullanıcı oluştur (şifre otomatik olarak telefon numarasının son 4 hanesi olacak)
      const userResponse = await api.post('/api/admin/users', {
        role: 'DRIVER',
        name: formData.name,
        email: `${formData.phone}@driver.bht.com`, // Otomatik email oluştur
        phone: formData.phone
      });

      const userId = userResponse.data.id;

      // 2. Şoför kaydı oluştur
      await api.post('/api/admin/drivers', {
        user_id: userId,
        is_active: true
      });

      // Formu temizle
      setFormData({
        name: '',
        phone: ''
      });

      // Listeyi yenile
      await loadWeeklyShifts();
      setShowAddModal(false);
      showSuccess('Şoför başarıyla eklendi!');

    } catch (error) {
      console.error('Şoför eklenemedi:', error);
      showError('Hata: ' + (error.response?.data?.detail || 'Şoför eklenemedi'));
    } finally {
      setLoading(false);
    }
  };

  // Vardiya shift type değiştirme (dropdown)
  const handleShiftTypeChange = (driverId, dayId, shiftType) => {
    const key = `${driverId}-${dayId}`;
    
    if (shiftType === 'OFF') {
      // İzinli olarak işaretle
      setOffDays(prev => ({ ...prev, [key]: true }));
      // Editing'den kaldır
      const newEditingShifts = { ...editingShifts };
      delete newEditingShifts[key];
      setEditingShifts(newEditingShifts);
    } else {
      // İzinli değil
      setOffDays(prev => {
        const newOffDays = { ...prev };
        delete newOffDays[key];
        return newOffDays;
      });
      // Shift type'ı kaydet
      setEditingShifts(prev => ({
        ...prev,
        [key]: shiftType
      }));
    }
  };

  const saveWeeklyPlan = async () => {
    try {
      setLoading(true);
      
      // Hem mevcut vardiyaları hem yeni girişleri topla
      const shiftsToSave = [];
      
      driversWithShifts.forEach(driver => {
        weekDays.forEach(day => {
          const key = `${driver.id}-${day.id}`;
          
          // İzinli mi kontrol et
          if (offDays[key]) {
            // İzinli (OFF) - vardiya kaydetme
            return;
          }
          
          // Shift type belirle
          let shiftType = null;
          
          if (editingShifts[key]) {
            // Kullanıcı değişiklik yaptı - shift type olarak kaydedilmiş
            shiftType = editingShifts[key];
          } else if (driver.weeklyShifts[day.id]) {
            // Mevcut vardiya var - shift type'ını al
            shiftType = driver.weeklyShifts[day.id].shift_type;
          }
          
          // Shift type varsa saat bilgilerini al ve kaydet
          if (shiftType) {
            const times = getShiftTimes(shiftType);
            
            if (times) {
              shiftsToSave.push({
                driver_id: driver.id,
                shift_date: day.fullDate,
                start_time: `${times.start}:00`,
                end_time: `${times.end}:00`,
                shift_type: shiftType,
                is_available: true
              });
            }
          }
        });
      });
      
      if (shiftsToSave.length === 0) {
        showWarning('Kaydedilecek vardiya bulunamadı. Lütfen en az bir vardiya girin.');
        return;
      }
      
      console.log('📊 KAYDEDİLECEK VARDİYALAR:');
      console.log('  Toplam şoför:', driversWithShifts.length);
      console.log('  Toplam vardiya:', shiftsToSave.length);
      console.log('  Beklenen (max):', driversWithShifts.length * 7);
      console.log('  Detay:', shiftsToSave);
      console.log('  İzinli günler:', offDays);
      console.log('  Editing shifts:', editingShifts);
      
      // API'ye gönder (Local timezone)
      const weekStartStr = `${currentWeekStart.getFullYear()}-${String(currentWeekStart.getMonth() + 1).padStart(2, '0')}-${String(currentWeekStart.getDate()).padStart(2, '0')}`;
      await api.post('/api/shifts/weekly-save', {
        week_start: weekStartStr,
        shifts: shiftsToSave
      });
      
      showSuccess(`Haftalık plan başarıyla kaydedildi! (${shiftsToSave.length} vardiya)`);
      
      // Editing state'i temizle (offDays'i tutuyoruz)
      setEditingShifts({});
      
      // Planı yeniden yükle
      await loadWeeklyShifts();
      
    } catch (error) {
      console.error('Haftalık plan kaydedilemedi:', error);
      showError('Haftalık plan kaydedilemedi: ' + (error.response?.data?.detail || 'Bilinmeyen hata'));
    } finally {
      setLoading(false);
    }
  };
  
  // Shift type'tan saat bilgisini al
  const getShiftTimes = (shiftType) => {
    const shifts = {
      'A_SHIFT': { start: '08:00', end: '17:30' },
      'B_SHIFT': { start: '15:00', end: '00:30' },
      'C_SHIFT': { start: '23:00', end: '08:00' }
    };
    return shifts[shiftType] || null;
  };


  const handleDeleteDriver = async (driverId, driverName) => {
    // Modern onay penceresi
    const confirmDelete = await showNotification(
      'Transfer Sistemi Mesajı',
      `"${driverName || 'İsimsiz'}" adlı şoförü kalıcı olarak silmek istediğinizden emin misiniz?\n\nBu işlem:\n• Şoförün tüm vardiya şablonlarını silecek\n• Geçmiş transfer kayıtlarındaki bağlantıyı kaldıracak\n• Kullanıcı hesabını da silecek\n\nBu işlem geri alınamaz!`,
      'warning'
    );

    if (!confirmDelete) {
      return;
    }

    try {
      setLoading(true);
      console.log(`🗑️ Şoför silme isteği gönderiliyor: ID ${driverId}, İsim: ${driverName}`);
      
      await api.delete(`/api/admin/drivers/${driverId}`);
      
      // Başarılı mesajı
      console.log(`✅ Şoför başarıyla silindi: ${driverName}`);
      showSuccess('Şoför başarıyla silindi!');
      
      // Listeyi yenile
      await loadWeeklyShifts();
      
    } catch (error) {
      console.error('❌ Şoför silme hatası:', error);
      console.error('Hata detayları:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      
      // Hata mesajını kullanıcı dostu hale getir
      let errorMessage = 'Şoför silinirken bir hata oluştu.';
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.status === 400) {
        errorMessage = 'Şoförün aktif transferleri bulunuyor. Önce bu transferleri tamamlayın veya başka şoföre atayın.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Şoför bulunamadı.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Yetkiniz bulunmuyor. Lütfen tekrar giriş yapın.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Sunucu hatası. Lütfen sistem yöneticisine başvurun.';
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        errorMessage = 'Bağlantı hatası. İnternet bağlantınızı kontrol edin.';
      }
      
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() + (direction === 'prev' ? -7 : 7));
    setCurrentWeekStart(newDate);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-800">Vardiya Yönetimi</h1>
            </div>
            <div className="flex items-center gap-3">
              <button 
                onClick={() => setShowAddModal(true)}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2"
              >
                <Plus size={18} />
                <span>Şoför Ekle</span>
              </button>
              <button 
                onClick={saveWeeklyPlan}
                disabled={loading}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                <Download size={18} />
                <span>{loading ? 'Kaydediliyor...' : 'Haftalık Planı Kaydet'}</span>
              </button>
            </div>
          </div>

          {/* Date Navigation */}
          <div className="flex items-center justify-between bg-blue-50 rounded-lg p-4">
            <button 
              onClick={() => navigateWeek('prev')}
              className="p-2 hover:bg-blue-100 rounded-lg transition-colors"
            >
              <ChevronLeft size={20} className="text-blue-600" />
            </button>
            <div className="flex items-center gap-3">
              <Calendar size={20} className="text-blue-600" />
              <span className="font-semibold text-gray-800">
                {(() => {
                  const weekEnd = new Date(currentWeekStart);
                  weekEnd.setDate(currentWeekStart.getDate() + 6);
                  const startStr = currentWeekStart.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long' });
                  const endStr = weekEnd.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' });
                  return `${startStr} - ${endStr}`;
                })()}
              </span>
            </div>
            <button 
              onClick={() => navigateWeek('next')}
              className="p-2 hover:bg-blue-100 rounded-lg transition-colors"
            >
              <ChevronRight size={20} className="text-blue-600" />
            </button>
          </div>

          {/* Info Alert */}
          <div className="mt-4 bg-blue-100 border-l-4 border-blue-500 p-4 rounded-r-lg">
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white text-xs font-bold">i</span>
              </div>
              <p className="text-sm text-blue-800">
                Vardiyaları doldurun ve kaydedin. İzinli günler için "İzinli" kutusunu işaretleyin.
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="p-6">
        {driversWithShifts.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-500">
            Henüz kayıtlı şoför bulunmuyor
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 sticky left-0 bg-gray-50 z-20 min-w-[250px] shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                      <div className="flex items-center gap-2">
                        <Users size={18} />
                        SÜRÜCÜ BİLGİLERİ
                      </div>
                    </th>
                    {weekDays.map((day) => (
                      <th key={day.id} className="px-2 py-3 text-center min-w-[110px]">
                        <div className="flex flex-col items-center gap-0.5">
                          <span className="text-sm font-bold text-gray-800">{day.short}</span>
                          <span className="text-xs text-gray-500">{day.date}</span>
                        </div>
                      </th>
                    ))}
                    <th className="px-2 py-3 text-center text-sm font-semibold text-gray-700 min-w-[70px]">
                      İŞLEM
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {driversWithShifts.map((driver) => (
                    <tr key={driver.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-2 sticky left-0 bg-white z-20 border-r border-gray-100 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-white font-semibold text-xs">
                              {driver.user_name ? driver.user_name.split(' ').map(n => n[0]).join('') : '??'}
                            </span>
                          </div>
                          <div>
                            <div className="font-medium text-sm text-gray-800">{driver.user_name || 'İsimsiz'}</div>
                            <div className="text-xs text-gray-500 flex items-center gap-1">
                              <Clock size={12} />
                              {driver.user_phone || '-'}
                            </div>
                          </div>
                        </div>
                      </td>
                      {weekDays.map((day) => {
                        const shift = driver.weeklyShifts[day.id];
                        const key = `${driver.id}-${day.id}`;
                        const isOffDay = offDays[key];
                        
                        // Mevcut shift type'ı belirle
                        let currentShiftType = '';
                        if (isOffDay) {
                          currentShiftType = 'OFF';
                        } else if (editingShifts[key]) {
                          currentShiftType = editingShifts[key];
                        } else if (shift) {
                          currentShiftType = shift.shift_type;
                        }
                        
                        // Shift type'a göre renk
                        const getShiftColor = (shiftType) => {
                          if (shiftType === 'OFF' || isOffDay) return 'bg-red-100 text-red-700 border-red-300';
                          if (shiftType === 'A_SHIFT') return 'bg-blue-100 text-blue-700 border-blue-300';
                          if (shiftType === 'B_SHIFT') return 'bg-purple-100 text-purple-700 border-purple-300';
                          if (shiftType === 'C_SHIFT') return 'bg-indigo-100 text-indigo-700 border-indigo-300';
                          return 'bg-gray-50 text-gray-600 border-gray-200';
                        };
                        
                        return (
                          <td key={day.id} className="px-2 py-2">
                            <div className="flex flex-col items-center gap-1">
                              <select
                                value={currentShiftType}
                                onChange={(e) => handleShiftTypeChange(driver.id, day.id, e.target.value)}
                                className={`w-full rounded-md px-2 py-1.5 text-center text-xs font-medium transition-colors border cursor-pointer ${getShiftColor(currentShiftType)}`}
                              >
                                <option value="">Seç...</option>
                                <option value="A_SHIFT">A (08:00-17:30)</option>
                                <option value="B_SHIFT">B (15:00-00:30)</option>
                                <option value="C_SHIFT">C (23:00-08:00)</option>
                                <option value="OFF">İzinli</option>
                              </select>
                            </div>
                          </td>
                        );
                      })}
                      <td className="px-2 py-2">
                        <div className="flex justify-center">
                          <button 
                            onClick={() => handleDeleteDriver(driver.id, driver.user_name)}
                            className="px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded-md transition-colors border border-red-200"
                          >
                            Sil
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Add Driver Modal */}
      {showAddModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowAddModal(false)}
        >
          <div 
            className="bg-white rounded-xl shadow-xl max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-800">Yeni Şoför Ekle</h3>
              <button 
                onClick={() => setShowAddModal(false)}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ad Soyad
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  placeholder="Örn: Ahmet Yılmaz"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Telefon Numarası
                </label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  placeholder="Örn: 0530 123 45 67"
                />
                <p className="mt-1 text-xs text-gray-500">
                  * Şifre otomatik olarak oluşturulacaktır
                </p>
              </div>
            </div>
            
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <button 
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
              >
                İptal
              </button>
              <button 
                onClick={handleSubmit}
                disabled={loading}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                <Plus size={18} />
                {loading ? 'Ekleniyor...' : 'Ekle'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VardiyaYonetimi;
